import { useMemo } from 'react';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { CultureCode, resolveEventLocale } from '@shared/hooks/useGeoLocale';
import { findAlpha3ByAlpha2 } from '@shared/hooks/useGeoLocale/countries';
import { buildCountryEntries, resolveCashFundLocale } from '@shared/utils/currency/cashFundCountries';
import { useGetCashFundCurrenciesQuery } from '@shared/utils/currency/cashFundCurrencies.graphql.generated';

/** Structural subset of an event membership — only what eligibility needs, so callers can pass
 * `useEventMemberships()` results straight through without mapping. */
export type CashOnlyEventCandidate = Readonly<{
  event?: Readonly<{
    id: string;
    info?: Readonly<{ locale?: Maybe<string> }> | null;
    registry?: Readonly<{ disableIntlRegistry?: Maybe<boolean> }> | null;
  }> | null;
}>;

export type CashOnlyEventsResult = Readonly<{
  /** Ids of the given events whose registry is cash-fund-only. Empty while `loading`. */
  cashOnlyEventIds: ReadonlySet<string>;
  loading: boolean;
}>;

/**
 * Decides, for several events at once, which ones get the lean international registry experience
 * (cash-fund-only, no US catalog) — the multi-event counterpart of
 * {@link useInternationalRegistryExperience}.
 *
 * That hook resolves a single event through EventInfoProvider, so it cannot answer the question the
 * public Shop asks: of the N events this account owns, which can't accept a store product? Here the
 * per-event signals are the event's own `info.locale` and `registry.disableIntlRegistry` (the CX
 * kill-switch, which forces an event back to the US experience), both passed in by the caller. The
 * remaining inputs are account-wide — the two feature flags carry no per-event targeting, and the
 * supported cash fund currency list is global. Nothing here depends on event context, so the Shop
 * stays a public surface: anonymous visitors never trigger an event query.
 *
 * Callers must fail open while `loading` — blocking a US event is worse than briefly allowing an
 * international one, and the set is empty until every input has resolved.
 */
export const useCashOnlyEvents = (events: ReadonlyArray<CashOnlyEventCandidate>): CashOnlyEventsResult => {
  const [isLeanInternationalRegistryEnabled, isLeanFlagLoading] = useIsFeatureEnabled('leanInternationalRegistryEnabled');
  const [isCurrencyI18nEnabled, isCurrencyFlagLoading] = useIsFeatureEnabled('registryCashFundCurrency18nEnabled');

  const skipCurrencies = !isLeanInternationalRegistryEnabled || !isCurrencyI18nEnabled || events.length === 0;
  const { data, loading: currenciesLoading } = useGetCashFundCurrenciesQuery({
    batchMode: 'fast',
    fetchPolicy: 'cache-first',
    skip: skipCurrencies,
  });

  const entries = useMemo(() => buildCountryEntries(data?.getCashFundCurrencies.currencyDescriptors ?? []), [data]);

  const loading = isLeanFlagLoading || isCurrencyFlagLoading || (!skipCurrencies && currenciesLoading);

  const cashOnlyEventIds = useMemo(() => {
    if (loading || skipCurrencies) return new Set<string>();

    return new Set(
      events.flatMap(({ event }) => {
        if (!event || event.registry?.disableIntlRegistry) return [];
        const locale = resolveEventLocale(event.info?.locale);
        if (!locale || locale.culture === CultureCode.US) return [];
        const preferredCountryCode = findAlpha3ByAlpha2(locale.culture);
        return resolveCashFundLocale(entries, locale.currency, preferredCountryCode) ? [event.id] : [];
      }),
    );
  }, [events, entries, loading, skipCurrencies]);

  return { cashOnlyEventIds, loading };
};
